import type { AuthTokens, User } from "@/types/domain";
import { gql } from "@apollo/client";
import { apolloClient } from "@/lib/apollo/client";
import { GET_EVENT } from "@/graphql/queries/events";
import { getEventBasePath, getSessionCookie, hasEventSession } from "./session";
// $data: LoginInput!
const LOGIN = gql`
  mutation Login($data: LoginInput!) {
    login(data: $data)
  }
`;

const ME = gql`
  query me {
    me
  }
`;
// ─── Response types ───────────────────────────────────────────────
export interface LoginSuccess {
  ok: true;
  user: User;
  tokens?: AuthTokens;
  company?: any | null;
  companies?: any[] | null;
}

export interface LoginFailure {
  ok: false;
  error: string;
}

export type LoginResult = LoginSuccess | LoginFailure;

// ─── Auth service ─────────────────────────────────────────────────
export const authService = {
  /**
   * Authenticate with email + password.
   * The backend issues and expires the FenixToken cookie.
   */
  async login(email: string, password: string): Promise<LoginResult> {
    if (!email || !password) {
      return { ok: false, error: "Email and password are required." };
    }

    try {
      const previousCookie = getSessionCookie();
      const { data } = await apolloClient.mutate<{
        login: { success: boolean } | null;
      }>({
        mutation: LOGIN,
        variables: {
          data: {
            email,
            password,
          },
        },
        errorPolicy: "none",
      });

      if (data?.login?.success !== true) {
        return {
          ok: false,
          error: "Login was rejected. Please check your credentials.",
        };
      }
      const sessionCookie = getSessionCookie();
      if (!sessionCookie || sessionCookie === previousCookie) {
        return {
          ok: false,
          error:
            "Login completed, but the session cookie was not received or updated. Check your browser cookie settings and API configuration.",
        };
      }

      await apolloClient.clearStore();
      const meData = await apolloClient.query<{
        me: User & {
          company?: LoginSuccess["company"];
          companies?: LoginSuccess["companies"];
        };
      }>({
        query: ME,
        fetchPolicy: "network-only",
        errorPolicy: "none",
      });

      if (!meData?.data?.me) {
        return {
          ok: false,
          error:
            "Login completed, but the session could not be verified. Please sign in again.",
        };
      }

      return {
        ok: true,
        user: meData.data.me,
        company: meData.data.me?.company ?? null,
        companies: meData.data.me?.companies ?? null,
      };
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof Error
            ? error.message
            : "Unable to login. Please try again.",
      };
    }
  },

  async restoreEventSession(): Promise<LoginResult> {
    if (!hasEventSession()) {
      return { ok: false, error: "Please sign in to this event." };
    }

    const basePath = getEventBasePath();
    if (basePath !== "/") {
      const { data } = await apolloClient.query<{
        getEvent: { hasAccess: boolean } | null;
      }>({
        query: GET_EVENT,
        variables: { eventID: basePath.slice(1) },
        fetchPolicy: "network-only",
        errorPolicy: "none",
      });
      if (data?.getEvent?.hasAccess !== true) {
        return {
          ok: false,
          error: "Please sign in to verify your event access.",
        };
      }
    }

    const { data } = await apolloClient.query<{
      me: User & {
        company?: LoginSuccess["company"];
        companies?: LoginSuccess["companies"];
      };
    }>({
      query: ME,
      fetchPolicy: "network-only",
      errorPolicy: "none",
    });
    if (!data?.me) {
      return {
        ok: false,
        error: "Your session has expired. Please sign in again.",
      };
    }
    return {
      ok: true,
      user: data.me,
      company: data.me.company ?? null,
      companies: data.me.companies ?? [],
    };
  },
};
